import {forwardRef} from 'react';
import cx from 'classnames';

import {Button, Carousel} from '#component/base';
import {CheckInStat} from '#component/partial/CheckInStat';
import {useCarousel, useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {CheckInCarouselProps} from './CheckInCarousel.types';

import * as css from './CheckInCarousel.css';

/**
 * This component renders a horizontally-scrollable list of `CheckInStat`s,
 * within a `Carousel`, allowing the user to view their number of completed
 * check-ins over time.
 *
 * On larger viewports, forward and back buttons are rendered on either side of
 * the carousel.
 */
export const CheckInCarousel = forwardRef<HTMLDivElement, CheckInCarouselProps>(
  ({className, checkIns = [], ...otherProps}, ref) => {
    const {isMobile} = useViewportSize();
    const {
      goToNextSlide,
      goToPreviousSlide,
      ref: carouselRef,
    } = useCarousel({gap: 16, slideWidth: 60});

    return (
      <div className={cx(sprinkles({flex: 'flex', gap: 12}), className)} ref={ref} {...otherProps}>
        {!isMobile && (
          <Button
            aria-label="Go to previous slide"
            icon="ArrowLeft"
            onClick={goToPreviousSlide}
            variant="secondary"
          />
        )}

        <Carousel
          className={css.carousel}
          paddings={[30, 30, 30]}
          ref={carouselRef}
          widths={['100%', '100%', '100%']}>
          {checkIns.map((checkIn, i) => (
            <CheckInStat {...checkIn} key={i} />
          ))}
        </Carousel>

        {!isMobile && (
          <Button
            aria-label="Go to next slide"
            icon="ArrowRight"
            onClick={goToNextSlide}
            variant="secondary"
          />
        )}
      </div>
    );
  },
);
